import { DatePicker } from "@heroui/date-picker";
import { CalendarDate } from "@internationalized/date";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FieldValues,
  Path,
  PathValue,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";

type DateFieldProps<T extends FieldValues> = {
  name: string;
  label: string;
  watch: UseFormWatch<T>;
  setValue: UseFormSetValue<T>;
  register: UseFormRegister<T>;
  required?: boolean;
  labelPlacement?: "inside" | "outside" | "outside-left";
};

const getCurrentDate = () => {
  const today = new Date();
  return new CalendarDate(
    today.getFullYear(),
    today.getMonth() + 1,
    today.getDate(),
  );
};

const defaultPlaceholderValue = new CalendarDate(2024, 1, 1);

export function DateField<T extends FieldValues>({
  name,
  label,
  watch,
  setValue,
  required = false,
  labelPlacement = "outside",
}: DateFieldProps<T>) {
  const { t } = useTranslation();
  const translatedLabel = t(label, { defaultValue: label });
  const uuid = useId();
  // we use this state to know if the popover is open
  const [isOpen, setIsOpen] = useState(false);

  // we use this state to know the placeholder value
  // when we hover on the button, we set the placeholder to the current date
  // when we leave the button, we set the placeholder to the default value
  // when the popover is open, we don't change the placeholder
  // when the popover is closed, we set the placeholder to the default value
  const [placeholderValue, setPlaceholderValue] = useState(
    defaultPlaceholderValue,
  );

  const currentValue = watch(name as Path<T>);
  const popoverId = `date-picker-popover-${uuid}`;

  const handleChange = (date: any) => {
    setValue(name as Path<T>, date as PathValue<T, Path<T>>, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const ref = useRef<HTMLDivElement>(null);

  // Add hover event listener on the button to detect potential opening
  useEffect(() => {
    if (ref.current) {
      // Get the button which is the sibling of ref.current
      const button = ref.current.nextElementSibling as HTMLButtonElement;
      if (button) {
        const handleButtonHover = () => {
          // Only update if not already open
          if (!isOpen) {
            setPlaceholderValue(getCurrentDate());
          }
        };

        const handleButtonLeave = () => {
          // Only reset if popover is not open
          if (!isOpen) {
            setPlaceholderValue(defaultPlaceholderValue);
          }
        };

        button.addEventListener("mouseenter", handleButtonHover);
        button.addEventListener("mouseleave", handleButtonLeave);

        return () => {
          button.removeEventListener("mouseenter", handleButtonHover);
          button.removeEventListener("mouseleave", handleButtonLeave);
        };
      }
    }
  }, [isOpen]);

  // MutationObserver to detect popover closing
  // event onOpenChange don't work so we need to do it by hand
  useEffect(() => {
    const checkPopoverClosed = () => {
      const popoverElement = document.getElementById(popoverId);
      const popoverIsOpen = popoverElement !== null;

      // Only handle closing
      if (!popoverIsOpen && isOpen) {
        setIsOpen(false);
        setPlaceholderValue(defaultPlaceholderValue);
      }
    };

    // Create MutationObserver to watch for DOM changes
    const observer = new MutationObserver(() => {
      checkPopoverClosed();
    });

    // Start observing the document body for child additions/removals
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [isOpen, popoverId]);

  return (
    <DatePicker
      key={name}
      label={translatedLabel}
      variant="bordered"
      labelPlacement={labelPlacement}
      size="sm"
      className="min-w-36"
      value={currentValue || null}
      onChange={handleChange}
      color="primary"
      ref={ref}
      onOpenChange={(open) => {
        setIsOpen(open);
      }}
      isRequired={required}
      popoverProps={{
        id: popoverId,
      }}
      classNames={{
        base: "tmt-0 [&>span]:text-blue-600",
        timeInput:
          "text-blue-600 group-data-[filled-within=true]:text-blue-600",
        timeInputLabel:
          "bg-white hover:bg-white hover:border-slate-400 data-[hover=true]:bg-white group-data-[focus=true]:bg-white group-data-[focus=true]:border-primary border-2 shadow-sm",
      }}
      // see https://github.com/heroui-inc/heroui/issues/2913
      // also https://github.com/adobe/react-spectrum/issues/3256
      placeholderValue={placeholderValue as any}
    />
  );
}
